<template>
  <div class="dropdown-menu" v-show="open">
    <div class="dropdown-search" v-if="filterable">
      <TransitInput v-model="search" :prefixIcon="resolvedPrefixIcon" :placeholder="resolvedSearchPlaceholder" />
    </div>
    <div class="dropdown-list hidden-scrollbar">
      <div v-if="filteredOptions.length === 0 && filterable" class="dropdown-empty">
        <img :src="resolvedEmptyIcon" class="empty-icon" alt="" />
        <span>{{ $t("noSearchContent") }}</span>
      </div>
      <div v-for="item in filteredOptions" :key="itemKey(item)" class="dropdown-item" @click="onSelect(item)">
        <div class="dropdown-item-left">
          <el-image v-if="itemIcon(item)" :src="itemIcon(item)">
            <div slot="error" class="image-error-slot">
              <img :src="resolvedLoadingFailedIcon" alt="" />
            </div>
          </el-image>
          <el-tooltip
            v-if="isShowTooltip"
            :content="itemLabel(item)"
            :disabled="!showTooltipMap[itemKey(item)]"
            manual
            v-model="showTooltipMap[itemKey(item)]"
            placement="top"
          >
            <span
              class="text-ellipsis"
              @mouseenter="updateOverflowTooltip($event, itemKey(item))"
              @mouseleave="hideOverflowTooltip(itemKey(item))"
              @click="onSelect(item)"
            >
              {{ itemLabel(item) }}
            </span>
          </el-tooltip>
          <span class="text-ellipsis" v-else>{{ itemLabel(item) }}</span>
        </div>
        <img class="selected-icon" v-if="isSelected(item)" :src="resolvedSelectedIcon" alt="" />
      </div>
    </div>
    <slot name="button"></slot>
  </div>
</template>

<script>
import TransitInput from "../common/TransitInput.vue";
import overflowTooltip from "../../mixins/overflowTooltip";

export default {
  name: "DropdownSelectMenu",
  components: { TransitInput },
  mixins: [overflowTooltip],
  props: {
    open: {
      type: Boolean,
      default: false,
    },
    value: null,
    options: {
      type: Array,
      required: true,
    },
    searchPlaceholder: {
      type: String,
      default: "",
    },
    prefixIcon: {
      type: String,
      default: "",
    },
    emptyIcon: {
      type: String,
      default: "",
    },
    selectedIcon: {
      type: String,
      default: "",
    },
    loadingFailedIcon: {
      type: String,
      default: "",
    },
    keyField: {
      type: String,
      default: "hid",
    },
    selectedField: {
      type: String,
      default: "hid",
    },
    labelField: {
      type: String,
      default: "title",
    },
    iconField: {
      type: String,
      default: "icon_url",
    },
    searchValueField: {
      type: String,
      default: "value",
    },
    filterable: {
      type: Boolean,
      default: true,
    },
    isShowTooltip: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      search: "",
    };
  },
  computed: {
    resolvedSearchPlaceholder() {
      if (this.searchPlaceholder) return this.searchPlaceholder;
      if (typeof this.$t === "function") return this.$t("searchNetworks");
      return "";
    },
    resolvedSelectedIcon() {
      return this.selectedIcon || require(`../../assets/common/correct-blue.png`);
    },
    resolvedEmptyIcon() {
      return this.emptyIcon || require(`../../assets/no-data/no-data-3${this.darkModeImg}.png`);
    },
    resolvedPrefixIcon() {
      return this.prefixIcon || require(`../../assets/search/search${this.darkModeImg}.png`);
    },
    resolvedLoadingFailedIcon() {
      return this.loadingFailedIcon || require(`../../assets/placeholder/Loading_failed${this.darkModeImg}.png`);
    },
    filteredOptions() {
      const s = this.search.trim().toLowerCase();
      return this.options.filter(item => {
        const title = (item?.[this.labelField] ?? "").toString().toLowerCase();
        const valueText = (item?.[this.searchValueField] ?? "").toString().toLowerCase();
        return title.includes(s) || valueText.includes(s);
      });
    },
  },
  methods: {
    itemKey(item) {
      return item?.[this.keyField];
    },
    itemLabel(item) {
      return item?.[this.labelField] ?? "";
    },
    itemIcon(item) {
      return item?.[this.iconField] ?? "";
    },
    isSelected(item) {
      return item?.[this.selectedField] === this.value;
    },
    onSelect(item) {
      this.search = "";
      this.$emit("select", item);
    },
  },
};
</script>

<style lang="less" scoped>
.dropdown-menu {
  position: absolute;
  right: 0;
  background: var(--bg-40);
  width: 240px;
  border-radius: 12px;
  box-shadow: 0px 2px 20px 0px var(--box-shadow-3);
  margin-top: 8px;
  z-index: 10;
  overflow: hidden;

  .dropdown-search {
    padding: 16px 16px 8px 16px;
    /deep/ .transit-input {
      height: 36px;
      padding: 0 12px 0 8px;
      background: var(--bg-64);
      border: 1px solid var(--border-17);

      .input-field {
        padding: 0 8px 0 0;
        font-size: 12px;
      }
    }
  }

  .dropdown-list {
    height: 247px;
    max-height: 247px;
    overflow-y: auto;

    .dropdown-item {
      height: 48px;
      display: flex;
      align-items: center;
      gap: 0 8px;
      justify-content: space-between;
      padding: 0 16px;
      cursor: pointer;

      &:hover {
        background: var(--bg-39);
      }

      .dropdown-item-left {
        flex: 1 1 auto;
        min-width: 0;
        display: flex;
        align-items: center;
        gap: 0 12px;

        span {
          font-size: 12px;
          color: var(--color-7);
        }

        /deep/ .el-image {
          width: 20px;
          height: 20px;
          border-radius: 10px;

          .image-error-slot {
            width: 20px;
            height: 20px;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 10px;

            img {
              width: 20px;
              height: 20px;
            }
          }
        }
      }

      .selected-icon {
        flex: 0 0 16px;
        width: 16px;
        height: 16px;
      }
    }

    .dropdown-empty {
      display: flex;
      flex-direction: column;
      align-items: center;
      margin-top: 58px;
      color: #aaaaaa;
      font-size: 14px;

      .empty-icon {
        width: 56px;
        height: 56px;
      }

      span {
        margin-top: 8px;
        font-size: 12px;
        font-weight: 500;
        color: var(--color-27);
      }
    }
  }
}
</style>
